import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../lib/translations';
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, language } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    pincode: '247776',
    language: 'en'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    return /^[6-9]\d{9}$/.test(phone);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear errors on change
    if (name === 'email') setEmailError('');
    if (name === 'phone') setPhoneError('');
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError(language === 'hi' ? 'कृपया वैध ईमेल दर्ज करें' : 'Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhone(formData.phone)) {
      setPhoneError(language === 'hi' ? 'कृपया वैध 10 अंकों का मोबाइल नंबर दर्ज करें' : 'Please enter a valid 10-digit mobile number');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!validateEmail(formData.email)) {
      setEmailError(language === 'hi' ? 'कृपया वैध ईमेल दर्ज करें' : 'Please enter a valid email address');
      toast.error(language === 'hi' ? 'कृपया वैध ईमेल दर्ज करें' : 'Please enter a valid email address');
      return;
    }

    // Validate phone
    if (!validatePhone(formData.phone)) {
      setPhoneError(language === 'hi' ? 'कृपया वैध 10 अंकों का मोबाइल नंबर दर्ज करें' : 'Please enter a valid 10-digit mobile number');
      toast.error(language === 'hi' ? 'कृपया वैध 10 अंकों का मोबाइल नंबर दर्ज करें' : 'Please enter a valid 10-digit mobile number');
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      toast.error(language === 'hi' ? 'पासवर्ड कम से कम 6 अक्षर का होना चाहिए' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(formData);
      toast.success(language === 'hi' ? 'खाता बनाया गया!' : 'Account created!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'hi' ? 'रजिस्ट्रेशन विफल' : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-white px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">AV</span>
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-slate-900">AV Mart</h1>
              <p className="text-xs text-slate-500">Shamli</p>
            </div>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {language === 'hi' ? 'नया खाता बनाएं' : 'Create an account'}
          </h2>
          <p className="text-slate-500 mb-6">
            {language === 'hi' ? 'अभी रजिस्टर करें और खरीदारी शुरू करें' : 'Register now and start shopping'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">{t('name', language)}</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder={language === 'hi' ? 'आपका नाम' : 'Your name'}
                required
                className="mt-1"
                data-testid="name-input"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">{t('email', language)}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                placeholder="your@email.com"
                required
                className={`mt-1 ${emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                data-testid="email-input"
              />
              {emailError && (
                <p className="text-red-500 text-sm mt-1">{emailError}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone">{t('phone', language)}</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                maxLength={10}
                value={formData.phone}
                onChange={handleChange}
                onBlur={handlePhoneBlur}
                placeholder="9876543210"
                required
                className={`mt-1 ${phoneError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                data-testid="phone-input"
              />
              {phoneError && (
                <p className="text-red-500 text-sm mt-1">{phoneError}</p>
              )}
            </div>

            {/* Pincode */}
            <div>
              <Label htmlFor="pincode">{t('pincode', language)}</Label>
              <Input
                id="pincode"
                name="pincode"
                type="text"
                maxLength={6}
                value={formData.pincode}
                onChange={handleChange}
                placeholder="247776"
                required
                className="mt-1"
                data-testid="pincode-input"
              />
            </div>

            {/* Language */}
            <div>
              <Label>{language === 'hi' ? 'भाषा' : 'Language'}</Label>
              <Select value={formData.language} onValueChange={(value) => setFormData({ ...formData, language: value })}>
                <SelectTrigger className="mt-1" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="hi">हिंदी</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password">{t('password', language)}</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {language === 'hi' ? 'कम से कम 6 अक्षर' : 'At least 6 characters'}
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700 h-12"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  {t('register', language)}
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-slate-500 mt-6">
            {t('haveAccount', language)}{' '}
            <Link to="/login" className="text-green-600 hover:text-green-700 font-medium" data-testid="login-link">
              {t('login', language)}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
